// Signing in at a kiosk in a real browser, the station sign-in handoff (ADR-0051, spec/07 §4.5, spec/12 §3.2,
// F-KIO-20).
//
// Google refuses OAuth inside an embedded web view (`disallowed_useragent`), so the kiosk's WebView2 can never run a
// Google, Discord or Steam sign-in itself. A **handoff** carries the sign-in out of the kiosk and back:
//
//   1. The kiosk (kiosk token) creates a handoff: a random, single-use secret bound to this station, alive for three
//      minutes. `POST /v1/station/sign-in/handoffs`.
//   2. The player finishes signing in on the player web's `/station-sign-in` page, with the secret in the URL's
//      fragment, in a locked-down browser window the station opens above the kiosk, or on their own phone from the
//      QR the kiosk shows. Any way in the player web offers works there, and a new player accepts the terms there.
//   3. The page, signed in, confirms the handoff with the player's own session, after showing which venue and PC it
//      signs into. `POST /v1/me/station-sign-in`.
//   4. The kiosk polls `POST /v1/station/sign-in/handoffs/{handoffId}/collect` and, once, receives the same
//      `StationSignInResult` a password or a code gives, then proceeds exactly as after any other kiosk sign-in.
//
// The secret is never logged and never in a request path: it travels in request bodies and the URL fragment, which a
// browser never sends to a server. The handoff's `id` (not a secret) names it everywhere else.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { StationSignInResultSchema } from "./station-sign-in.js";
/** The providers a kiosk offers as a button that opens the sign-in browser (Google, Discord, Steam). */
export const STATION_BROWSER_SIGN_IN_PROVIDERS = ['google', 'discord', 'steam'];
export const StationBrowserSignInProviderSchema = z
    .enum(STATION_BROWSER_SIGN_IN_PROVIDERS)
    .openapi('StationBrowserSignInProvider', { description: 'A provider the kiosk offers through the sign-in browser.' });
/** How long a handoff lives, in seconds. */
export const SIGN_IN_HANDOFF_LIFETIME_SECONDS = 180;
/** How often the kiosk asks whether the handoff has been confirmed, in seconds. */
export const SIGN_IN_HANDOFF_POLL_SECONDS = 2;
/** The handoff secret: 32 random bytes, base64url. Only ever in a body or a URL fragment. */
export const SignInHandoffTokenSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{43}$/, 'A handoff secret')
    .openapi({ description: 'The handoff secret (256 random bits, base64url). Never logged, never in a request path.' });
/** The handoff's id: 16 random bytes, base64url. Not a secret. */
export const SignInHandoffIdSchema = z
    .string()
    .regex(/^[A-Za-z0-9_-]{22}$/, 'A handoff id')
    .openapi({ description: 'The handoff’s id. Names it in a path, a log and the audit; not a secret.' });
export const CreateStationSignInHandoffSchema = z
    .object({
    /**
     * The provider button the player tapped, when they tapped one: the player web goes straight to it. Absent for
     * "Sign in with your phone", where the page offers every way in.
     */
    provider: StationBrowserSignInProviderSchema.optional(),
})
    .strict()
    .openapi('CreateStationSignInHandoff');
export const StationSignInHandoffSchema = z
    .object({
    id: SignInHandoffIdSchema,
    token: SignInHandoffTokenSchema,
    /**
     * The player web page that finishes it, the secret in the fragment: `https://play.{domain}/station-sign-in#<token>`
     * (with `?via=<provider>` when a provider was asked for). What the kiosk's QR encodes. The station opens its own
     * copy of this URL, built from its own configuration and the secret, never this string.
     */
    url: z.string().url(),
    expiresAt: IsoDateTimeSchema,
    /** How often to collect, in seconds. */
    pollIntervalSeconds: z.number().int().positive(),
})
    .openapi('StationSignInHandoff', { description: 'A sign-in to finish in a browser, for this station, once.' });
export const STATION_SIGN_IN_HANDOFF_STATES = ['waiting', 'signed-in', 'refused', 'expired', 'cancelled'];
export const StationSignInHandoffStatusSchema = z
    .object({
    /**
     * `waiting`, not confirmed yet; ask again. `signed-in`, `result` is the sign-in, handed out **once**: the next
     * collect answers `expired`. `refused`, the player confirmed but may not sign in here; `refusal` says why, in the
     * codes a kiosk sign-in already uses. `expired`, past its three minutes, already collected, or unknown.
     * `cancelled`, the kiosk or the player cancelled it.
     */
    state: z.enum(STATION_SIGN_IN_HANDOFF_STATES),
    /** Present only with `signed-in`: exactly what a password or a code sign-in at this kiosk returns. */
    result: StationSignInResultSchema.optional(),
    /** Present only with `refused`: `player-banned`, `sign-up-disabled` or `not-on-site`. */
    refusal: z.enum(['player-banned', 'sign-up-disabled', 'not-on-site']).optional(),
    /**
     * Present only with `signed-in`: the player's gamer tag. The kiosk no longer asks "Signed in as {name}" (the owner
     * removed that step, ADR-0051); a client may use it to greet the player.
     */
    playerName: z.string().min(1).max(64).optional(),
})
    .openapi('StationSignInHandoffStatus');
/**
 * `station.handoff.ready` (a station command, spec/09 §6.2): the handoff this station made has an answer to
 * collect, signed in or refused. The station tells its kiosk, which collects at once; nothing of the answer rides on the
 * command itself, so the collect (station-authenticated, handed out once) stays the only way to read it.
 */
export const StationSignInHandoffReadyPayloadSchema = z
    .object({ handoffId: SignInHandoffIdSchema })
    .openapi('StationSignInHandoffReadyPayload', { description: 'A handoff this station made is ready to collect.' });
export const SignInHandoffTokenBodySchema = z.object({ token: SignInHandoffTokenSchema }).strict().openapi('SignInHandoffToken');
export const ConfirmSignInHandoffSchema = z
    .object({
    token: SignInHandoffTokenSchema,
    /**
     * Sign this browser out once the PC is signed in: true from the sign-in window the station opened (the page was
     * opened with `?via=`), so no web session is left in it even for the seconds before the station closes it. A phone
     * sends false or nothing and stays signed in.
     */
    signOutAfter: z.boolean().optional(),
})
    .strict()
    .openapi('ConfirmSignInHandoff');
/** The secret in the "Not you? End it" link the player is sent after a handoff sign-in: 256 bits, base64url. */
export const SignInHandoffEndTokenBodySchema = z
    .object({ token: SignInHandoffTokenSchema })
    .strict()
    .openapi('SignInHandoffEndToken');
export const SignInHandoffEndedSchema = z
    .object({
    /** True when a session was running on that PC for this player and is now ended; false when there was none to end. */
    ended: z.boolean(),
    venueName: z.string(),
    stationName: z.string(),
})
    .openapi('SignInHandoffEnded');
export const SignInHandoffViewSchema = z
    .object({
    /** The venue the PC is at, by its name. */
    venueName: z.string(),
    /** The PC, as the venue labels it ("PC 07", or its name). */
    stationName: z.string(),
    /** The provider the player tapped at the PC, when they tapped one. */
    provider: StationBrowserSignInProviderSchema.optional(),
    expiresAt: IsoDateTimeSchema,
})
    .openapi('SignInHandoffView', {
    description: 'Where a handoff signs in, shown before the player confirms it (so a QR shown out of context is caught).',
});
const HANDOFF_UNAVAILABLE = 'The handoff is unknown, past its three minutes, cancelled or already used. It never comes back.';
const STATION_AUTHENTICATION = 'Station-authenticated: `Authorization: Bearer <kiosk token>` (spec/07 §4.1). A user session is not accepted here.';
export const createStationSignInHandoffRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/handoffs',
    operationId: 'createStationSignInHandoff',
    tags: ['identity'],
    summary: 'Start a sign-in to finish in a browser or on a phone, for this station',
    description: `${STATION_AUTHENTICATION} Creates a single-use handoff bound to this station, alive for three minutes, and ` +
        'returns its secret and the player web URL that finishes it. Counted against the station’s sign-in attempts ' +
        '(ten a minute). A new one supersedes nothing: an unused handoff simply expires.',
    request: {
        body: { content: { 'application/json': { schema: CreateStationSignInHandoffSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StationSignInHandoffSchema, 'The handoff.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        409: problemResponse('The station or its venue cannot be read, so the page could not name the PC the player would sign in to. No handoff is made.', 'station-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const collectStationSignInHandoffRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/handoffs/{handoffId}/collect',
    operationId: 'collectStationSignInHandoff',
    tags: ['identity'],
    summary: 'Ask whether the player has finished signing in, and take the sign-in once',
    description: `${STATION_AUTHENTICATION} Only the station that created the handoff can collect it; any other station is told ` +
        '`expired`, as for a handoff that does not exist. `signed-in` carries the sign-in exactly once. Polled every ' +
        '`pollIntervalSeconds`; at most 60 collects a minute per station.',
    request: { params: z.object({ handoffId: SignInHandoffIdSchema }) },
    responses: {
        200: jsonResponse(StationSignInHandoffStatusSchema, 'Where the handoff stands.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelStationSignInHandoffRoute = createRoute({
    method: 'post',
    path: '/v1/station/sign-in/handoffs/{handoffId}/cancel',
    operationId: 'cancelStationSignInHandoff',
    tags: ['identity'],
    summary: 'Cancel a handoff this station created',
    description: `${STATION_AUTHENTICATION} The player chose Cancel, or left the lock screen. A handoff not yet confirmed can ` +
        'never be confirmed after this. Answers the same for one already gone, or another station’s.',
    request: { params: z.object({ handoffId: SignInHandoffIdSchema }) },
    responses: {
        204: { description: 'Cancelled, or nothing to cancel.' },
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const lookUpSignInHandoffRoute = createRoute({
    method: 'post',
    path: '/v1/station-sign-in/lookup',
    operationId: 'lookUpSignInHandoff',
    tags: ['identity'],
    summary: 'Which venue and PC a handoff signs in to',
    description: 'Unauthenticated: the secret is the authority. Answers the venue and the PC, so the player web can ask "Sign in ' +
        'to PC 07 at Koramangala?" before anything is confirmed. 30 lookups a minute per address.',
    request: { body: { content: { 'application/json': { schema: SignInHandoffTokenBodySchema } }, required: true } },
    responses: {
        200: jsonResponse(SignInHandoffViewSchema, 'Where it signs in.'),
        400: commonProblemResponses[400],
        409: problemResponse(HANDOFF_UNAVAILABLE, 'sign-in-handoff-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const confirmSignInHandoffRoute = createRoute({
    method: 'post',
    path: '/v1/me/station-sign-in',
    operationId: 'confirmSignInHandoff',
    tags: ['identity'],
    summary: 'Sign in at the PC that showed this handoff, as the signed-in player',
    description: 'Signed in (the player web’s session). Runs the checks every kiosk sign-in runs, a ban at the venue, across ' +
        'its organisation or on the Platform; for an account made during this handoff, whether the venue creates ' +
        'accounts at its PCs, and hands the sign-in to the station, which collects it once. A handoff is confirmed at ' +
        'most once, by one player. Ten confirms per player per ten minutes. Audited.',
    request: { body: { content: { 'application/json': { schema: ConfirmSignInHandoffSchema } }, required: true } },
    responses: {
        200: jsonResponse(SignInHandoffViewSchema, 'Signed in at that PC.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: problemResponse('The player is banned here, or the account was made during this handoff at a venue that does not create accounts ' +
            'at its PCs.', 'player-banned', 'sign-up-disabled', 'not-on-site'),
        409: problemResponse(HANDOFF_UNAVAILABLE, 'sign-in-handoff-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const endSignInHandoffSessionRoute = createRoute({
    method: 'post',
    path: '/v1/station-sign-in/end',
    operationId: 'endSignInHandoffSession',
    tags: ['identity'],
    summary: 'Not you? End the session a handoff started',
    description: 'Unauthenticated: the secret from the "You signed in at PC 07" notice is the authority, for 24 hours. Ends the ' +
        'session running on that PC for that player, if it started at or after the handoff, as a logout at the PC would. ' +
        'Answers `ended: false` when there is nothing to end. 10 a minute per address. Audited.',
    request: { body: { content: { 'application/json': { schema: SignInHandoffEndTokenBodySchema } }, required: true } },
    responses: {
        200: jsonResponse(SignInHandoffEndedSchema, 'Ended, or nothing to end.'),
        400: commonProblemResponses[400],
        409: problemResponse('The link is unknown or past its 24 hours.', 'sign-in-handoff-unavailable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=station-sign-in-handoff.js.map